/**
 * Add a passkey to the signed-in account (plan §3 Phase 4 · AD-13).
 *
 * INLINE, not a modal (§2.2.5 · AD-13). The account menu is a disclosure with
 * normal document tab order; nesting a dialog inside it would introduce the
 * focus trap that pattern deliberately does not have, and would put a second
 * ceremony surface between the trigger and its own panel. So this renders one
 * button plus its two feedback nodes, and the panel owns the layout.
 *
 * 204 IS THE SUCCESS RESPONSE (§2.2.2 · AD-10). `POST /auth/credentials`
 * answers 204 with no body, so success is decided from `res.ok` inside
 * `postAuthJson` — which resolves `undefined` here — and nothing on this path
 * may require a parsed body. The options leg is the opposite case: a ceremony
 * cannot start without its options, so a missing `data` record throws.
 *
 * It deliberately does NOT import `passkey-login.tsx`'s ERROR CLASSES. Those
 * are private to it, and exporting them to share three lines would turn an
 * internal taxonomy into a cross-component contract — the two surfaces
 * classify different failures (there is no bootstrap fallback here, and a 401
 * is ambiguous until refresh() says whether the session is actually gone).
 *
 * That argument is about the taxonomy and reaches no further: the envelope
 * unwrap carries no taxonomy at all, so it lives in `webauthn-options.ts` and
 * both surfaces call it with their own error factory.
 */
import {
  browserSupportsWebAuthn,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type MutableRefObject,
  type RefObject,
} from 'react';

import type { AppConfig } from '@/config';

import styles from './add-passkey.module.css';
import { useAuth, type SessionProbe } from './auth-context';
import {
  AuthFetchError,
  AuthNetworkError,
  postAuthJson,
  reportProgrammingError,
} from './auth-fetch';
import { focusIsUnclaimed } from './focus-ownership';
import { assertRegistrationOptions, unwrapOptionsEnvelope } from './webauthn-options';

/**
 * The nickname every self-service enrolment carries.
 *
 * Fixed rather than typed by the user so the ceremony needs no extra field —
 * one control, one click. It is a wire value the operator reads back in an
 * account list, so it is a constant and not a call-site literal: a rename that
 * only touched the POST body would otherwise pass as a refactor.
 */
export const ADD_PASSKEY_NICKNAME = 'Authenticator';

const ADD_LABEL = 'Add passkey';
const BUSY_LABEL = 'Adding passkey…';
const SUCCESS_COPY = 'Passkey added.';

const UNAUTHORIZED_STATUS = 401;
const RATE_LIMIT_STATUS = 429;

type AddPasskeyError =
  | 'cancelled'
  | 'unsupported'
  | 'network'
  | 'unauthorized'
  | 'rate-limited'
  | 'already-registered'
  | 'unknown';

/** Raised by the pre-check, so an unsupported context is never a plain Error. */
class WebAuthnUnsupportedError extends Error {
  constructor() {
    super('WebAuthn is not available in this browser or context.');
    this.name = 'WebAuthnUnsupportedError';
  }
}

/** The options envelope carried no `data` record — the ceremony cannot start. */
class AddPasskeyOptionsError extends Error {
  constructor() {
    super('Add-passkey options payload was missing its data record.');
    this.name = 'AddPasskeyOptionsError';
  }
}

/**
 * Classify off the TYPED status, never a message string.
 *
 * 429 is split out because the ADR-0011 consecutive-failure ban is per-IP and
 * shared with sign-in, so retrying inside the five-minute window burns nothing
 * but time. 401 is handled in `errorAfterFailure` — it is not a dedicated
 * class here because the add-credential routes reuse 401 for live-session
 * ceremony failures.
 */
function classifyError(err: unknown): AddPasskeyError {
  if (err instanceof AuthNetworkError) return 'network';
  if (err instanceof WebAuthnUnsupportedError) return 'unsupported';
  if (err instanceof AuthFetchError) {
    if (err.status === RATE_LIMIT_STATUS) return 'rate-limited';
    return 'unknown';
  }
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError' || err.name === 'AbortError') return 'cancelled';
    if (err.name === 'NotSupportedError') return 'unsupported';
    if (err.name === 'InvalidStateError') return 'already-registered';
  }
  return 'unknown';
}

/**
 * 401 is overloaded on the add-credential routes: `fail()` uses it for a
 * missing challenge and a failed attestation while the session is still valid.
 * Refreshing is what distinguishes expiry (menu should vanish) from a ceremony
 * that simply did not stick (generic retry).
 *
 * ONLY a confirmed 'unauthenticated' claims expiry. The probe's third answer —
 * 'unevaluable' — is what a rejected fetch or a non-OK non-401 response
 * returns, and in those cases the context deliberately RETAINS 'authenticated'.
 * Reading them as expiry (which a boolean forced) told a user whose session was
 * fine to sign in again, while the account menu stayed mounted around the
 * message contradicting it.
 */
async function errorAfterFailure(
  err: unknown,
  refresh: () => Promise<SessionProbe>,
): Promise<AddPasskeyError> {
  if (err instanceof AuthFetchError && err.status === UNAUTHORIZED_STATUS) {
    return (await refresh()) === 'unauthenticated' ? 'unauthorized' : 'unknown';
  }
  return classifyError(err);
}

function messageFor(error: AddPasskeyError): string {
  if (error === 'cancelled') return 'Passkey prompt was cancelled.';
  if (error === 'unsupported') return 'This browser does not support passkeys.';
  if (error === 'network') return 'Network error - try again.';
  if (error === 'unauthorized') return 'Your session has expired. Sign in again.';
  /* No duration: the wire carries none, and two independent producers can
     return this status — see password-login.tsx for the full reasoning. */
  if (error === 'rate-limited') return 'Too many attempts. Try again later.';
  if (error === 'already-registered') return 'This authenticator is already registered.';
  return 'Could not add a passkey. Try again.';
}

/** Unwrap AND field-check the envelope, so a bad payload is a domain failure. */
function credentialOptions(payload: unknown): unknown {
  const makeError = (): Error => new AddPasskeyOptionsError();
  const options = unwrapOptionsEnvelope(payload, makeError);
  assertRegistrationOptions(options, makeError);
  return options;
}

/**
 * The ceremony outlives this component, so every leg re-checks `isCurrent`.
 *
 * `WebAuthnAbortService.cancelCeremony()` is NOT sufficient on its own: it is
 * a no-op until `startRegistration` has installed a controller, so a dismiss
 * during the options fetch cancels nothing. The options promise then resolves
 * in a dead closure and pops an authenticator prompt for a surface the user
 * already closed — and approving it would enrol a credential they are never
 * shown, because the state writes are guarded and the confirmation never
 * paints. Bailing between legs is what actually closes that window.
 */
async function runAddPasskey(
  config: AppConfig,
  guards: { readonly isCurrent: () => boolean; readonly ownsCeremony: MutableRefObject<boolean> },
): Promise<void> {
  /* Pre-check first, so a non-secure context (an http:// LAN dev server, most
     often) is reported as unsupported instead of arriving as the library's
     plain-Error sentinel — which has `name === 'Error'` and would fall through
     to the generic "Try again" the context can never satisfy. */
  if (!browserSupportsWebAuthn()) throw new WebAuthnUnsupportedError();
  const { isCurrent, ownsCeremony } = guards;
  const envelope = await postAuthJson(config, 'credentials/options');
  if (!isCurrent()) return;
  /* Ownership is claimed only around the library call, because the abort
     service is a MODULE-LEVEL singleton shared with the login ceremony.
     Cancelling outside this window would abort a ceremony we never started. */
  ownsCeremony.current = true;
  const attestation = await startRegistration({
    optionsJSON: credentialOptions(envelope) as Parameters<
      typeof startRegistration
    >[0]['optionsJSON'],
  }).finally(() => {
    ownsCeremony.current = false;
  });
  if (!isCurrent()) return;
  /* Resolves `undefined` on the 204 — success must not require a body. */
  await postAuthJson(config, 'credentials', {
    response: attestation,
    nickname: ADD_PASSKEY_NICKNAME,
  });
}

interface AddPasskeyState {
  readonly busy: boolean;
  readonly done: boolean;
  readonly error: AddPasskeyError | null;
  readonly onAdd: () => Promise<void>;
}

function useAddPasskey(config: AppConfig): AddPasskeyState {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<AddPasskeyError | null>(null);
  /**
   * A ref, not the `busy` state, is what makes a double click one ceremony. A
   * guard reading `busy` reads a RENDER closure, and React 18 batches every
   * event in a task, so two clicks in one batch both see `busy === false`. The
   * cost here is worse than a duplicate POST: @simplewebauthn's
   * `WebAuthnAbortService` aborts the first ceremony when a second starts, so
   * the first promise rejects with an AbortError and paints a failure over a
   * ceremony the user never cancelled.
   */
  const inFlight = useRef(false);
  /**
   * A ceremony outlives the panel: it awaits an authenticator prompt the user
   * may take seconds to answer, and closing the disclosure — or navigating —
   * unmounts this. Without the guard a late resolution sets state on a dead
   * component. Mirrors the `isCurrent()` predicate in auth-context.tsx.
   */
  const mounted = useRef(true);
  /**
   * True only while OUR `startRegistration` call is outstanding.
   *
   * `WebAuthnAbortService` is a module-level singleton holding whichever
   * controller was created last, by this component or by the login ceremony,
   * and it is never cleared on completion. Cancelling unconditionally on
   * unmount therefore aborts, and nulls, state this component may not own.
   * Nothing reachable today interleaves the two — routing keeps them from
   * co-mounting — but that invariant is implicit and unenforced, and the
   * Phase 5 re-auth ceremony would silently break it.
   */
  const ownsCeremony = useRef(false);
  useEffect(() => {
    mounted.current = true;
    /* The ref OBJECT in a local, read as `.current` at cleanup time — copying
       the value here instead would capture it at mount, when no ceremony has
       started, and cancel nothing. */
    const owns = ownsCeremony;
    return () => {
      mounted.current = false;
      if (owns.current) WebAuthnAbortService.cancelCeremony();
    };
  }, []);

  const onAdd = useCallback(async () => {
    if (inFlight.current) return;
    /* Written BEFORE the first await, so the guard above is already closed when
       a second click from the same batch reaches it. */
    inFlight.current = true;
    setError(null);
    setDone(false);
    setBusy(true);
    try {
      await runAddPasskey(config, { isCurrent: () => mounted.current, ownsCeremony });
      if (mounted.current) setDone(true);
    } catch (err) {
      /* Classification must never be the reason feedback goes missing. If
         `refresh()` rejects, an unguarded await here would skip BOTH setError
         and the report, returning the button to idle with no alert at all and
         escaping the click handler as an unhandled rejection. */
      let kind: AddPasskeyError;
      try {
        kind = await errorAfterFailure(err, refresh);
      } catch {
        kind = classifyError(err);
      }
      if (mounted.current) setError(kind);
      /* The user still sees the classified copy; only a defect of ours escapes,
         asynchronously, after this component's state has settled. */
      reportProgrammingError(err);
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [config, refresh]);

  return { busy, done, error, onAdd };
}

/**
 * Return focus to the control once the ceremony settles.
 *
 * Disabling the focused button blurs it and drops focus to `<body>` — the same
 * fact the live region below exists for — so without this a cancelled prompt
 * leaves a keyboard or screen-reader user at the top of the document with the
 * disclosure still open and the next Tab restarting from the page. AD-13
 * requires focus to remain "sensible" after an AddPasskey cancel.
 *
 * Conditional on `focusIsUnclaimed` for the reason that predicate documents: a
 * user who tabbed on to Sign out while the prompt was open keeps that focus.
 */
function useRestoreFocusAfterCeremony(
  busy: boolean,
  controlRef: RefObject<HTMLButtonElement>,
): void {
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busy && focusIsUnclaimed()) {
      controlRef.current?.focus();
    }
    wasBusy.current = busy;
  }, [busy, controlRef]);
}

function statusCopy(busy: boolean, done: boolean): string {
  if (busy) return BUSY_LABEL;
  return done ? SUCCESS_COPY : '';
}

export interface AddPasskeyProps {
  readonly config: AppConfig;
}

export function AddPasskey({ config }: AddPasskeyProps): JSX.Element {
  const { busy, done, error, onAdd } = useAddPasskey(config);
  const controlRef = useRef<HTMLButtonElement>(null);
  const errorId = useId();
  useRestoreFocusAfterCeremony(busy, controlRef);

  return (
    <div className={styles['root']}>
      <button
        ref={controlRef}
        type="button"
        className={styles['button']}
        disabled={busy}
        /* Emitted on the branch that mounts the target, the same dangling-IDREF
           discipline `account-menu.tsx` applies to `aria-controls`. Focus is
           pulled back to this control once the ceremony settles, so without the
           association a user who missed the single alert announcement would sit
           on a button with no error context. */
        {...(error === null ? {} : { 'aria-describedby': errorId })}
        onClick={() => void onAdd()}
      >
        {busy ? BUSY_LABEL : ADD_LABEL}
      </button>
      {error !== null ? (
        <p id={errorId} className={styles['error']} role="alert">
          {messageFor(error)}
        </p>
      ) : null}
      {/*
       * EXACTLY ONE polite region, carrying both the pending announcement and
       * the confirmation. Two would compete for announcements and would move
       * the live-region count `add-passkey.test.tsx` pins for this surface.
       *
       * The header-level sweep in `chrome-a11y.test.tsx` pins a count too —
       * one region per action in the expanded panel, this one and
       * `set-password.tsx`'s — not merely that no NAMED region is added. This
       * comment used to claim the opposite, and a second unnamed region here
       * would now break that sweep as well as this file's own.
       *
       * WCAG 4.1.3 is why the busy half exists at all: a disabled button is
       * blurred and dropped from the a11y tree, so the label change to
       * 'Adding passkey…' announces nothing and focus falls to <body>.
       *
       * It is `visually-hidden` for both of those states and VISIBLE for the
       * confirmation — one node changing class, not a hidden node plus a
       * visible twin, so a sighted user gets feedback without the sentence
       * appearing twice in the accessible tree. Separate from the error alert
       * so a polite status cannot preempt an assertive one.
       */}
      <p className={done ? styles['success'] : 'visually-hidden'} role="status">
        {statusCopy(busy, done)}
      </p>
    </div>
  );
}
